import { createContext, useContext, useState } from "react";
import { ChevronDown } from "components/common/icons";
import { cn } from "lib/utils";
import styles from "./select.module.css";

type SelectContextValue = {
  selectedValue: string;
  onValueChange: (val: string) => void;
  open: boolean;
  setOpen: React.Dispatch<React.SetStateAction<boolean>>;
  placeholder?: string;
};

const SelectContext = createContext<SelectContextValue | null>(null);

interface SelectProps {
  value: string;
  onValueChange: (val: string) => void;
  placeholder?: string;
  children: React.ReactNode;
  className?: string;
}

export function Select({
  value,
  onValueChange,
  placeholder,
  children,
  className,
}: SelectProps) {
  const [open, setOpen] = useState(false);

  return (
    <SelectContext.Provider
      value={{
        selectedValue: value,
        onValueChange,
        open,
        setOpen,
        placeholder,
      }}
    >
      <div className={cn(styles.selectContainer, className)}>{children}</div>
    </SelectContext.Provider>
  );
}

interface SelectTriggerProps extends React.HTMLAttributes<HTMLButtonElement> {
  id?: string;
  disabled?: boolean;
}

export function SelectTrigger({
  children,
  id,
  disabled,
  ...props
}: SelectTriggerProps) {
  const ctx = useContext(SelectContext);
  if (!ctx) {
    throw new Error("SelectTrigger must be used within a <Select> component.");
  }

  const { open, setOpen } = ctx;

  function handleClick() {
    if (disabled) return;
    setOpen((prev) => !prev);
  }

  return (
    <button
      id={id}
      type="button"
      aria-haspopup="listbox"
      aria-expanded={open}
      onClick={handleClick}
      disabled={disabled}
      className={cn(
        styles.selectTrigger,
        disabled && styles.selectTriggerDisabled,
      )}
      {...props}
    >
      <span>{children}</span>
      <ChevronDown className={styles.selectTriggerIcon} />
    </button>
  );
}

interface SelectValueProps {
  placeholder?: string;
  children?: React.ReactNode;
}

export function SelectValue({ placeholder, children }: SelectValueProps) {
  const ctx = useContext(SelectContext);
  if (!ctx) {
    throw new Error("SelectValue must be used within a <Select> component.");
  }

  const { selectedValue, placeholder: contextPlaceholder } = ctx;

  const finalValue =
    selectedValue || placeholder || contextPlaceholder || children;

  return <>{finalValue}</>;
}

interface SelectContentProps {
  children: React.ReactNode;
}

export function SelectContent({ children }: SelectContentProps) {
  const ctx = useContext(SelectContext);
  if (!ctx) {
    throw new Error("SelectContent must be used within a <Select> component.");
  }
  const { open } = ctx;

  if (!open) return null;

  return (
    <div className={cn(styles.selectContent)} role="listbox" tabIndex={-1}>
      {children}
    </div>
  );
}

interface SelectItemProps {
  value: string;
  children: React.ReactNode;
}

export function SelectItem({ value, children }: SelectItemProps) {
  const ctx = useContext(SelectContext);
  if (!ctx) {
    throw new Error("SelectItem must be used within a <Select> component.");
  }

  const { onValueChange, setOpen, selectedValue } = ctx;

  function handleClick() {
    onValueChange(value);
    setOpen(false);
  }

  const isSelected = selectedValue === value;

  return (
    <div
      role="option"
      aria-selected={isSelected}
      onClick={handleClick}
      className={cn(styles.selectItem, isSelected && styles.selectItemSelected)}
    >
      {children}
    </div>
  );
}
